import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { QueryKeys } from 'src/constants';
import useCustomSnackbarGlobal from 'src/hooks/useSnackbarGlobal';
import ApiService from 'src/modules/api-service';
import {
  CreatePurchaseAgentRequest,
  PurchaseAgentListItem,
  UpdatePurchaseAgentRequest,
} from 'src/modules/generated/api';
import { PURCHASE_AGENT_ACTION_TARGET_STATE, PurchaseAgentAction } from 'src/modules/purchase-agent';

const getErrorCode = (error: unknown) =>
  axios.isAxiosError<{ errorCode?: string }>(error) ? error.response?.data?.errorCode : undefined;

export const usePurchaseAgents = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useQuery(
    [QueryKeys.purchaseAgents],
    () => ApiService.purchaseAgent.purchaseAgentControllerList().then((res) => res.data),
    { enabled },
  );

export const usePurchaseAgentDetail = (id?: string) =>
  useQuery(
    [QueryKeys.purchaseAgentDetail, id],
    () => ApiService.purchaseAgent.purchaseAgentControllerDetail(id!).then((res) => res.data),
    { enabled: !!id },
  );

export const useCreatePurchaseAgent = () => {
  const queryClient = useQueryClient();
  const snackbar = useCustomSnackbarGlobal();
  const { t } = useTranslation();

  return useMutation(
    (newPurchaseAgent: CreatePurchaseAgentRequest) =>
      ApiService.purchaseAgent.purchaseAgentControllerCreate(newPurchaseAgent),
    {
      onSuccess: () => {
        snackbar.showSuccess(t('purchaseAgent.messages.createSuccess'));
      },
      onError: (error) => {
        console.error('[usePurchaseAgents] createPurchaseAgent failed', error);
        snackbar.showError(t('purchaseAgent.messages.createError'));
      },
      onSettled: () => {
        queryClient.invalidateQueries([QueryKeys.purchaseAgents]);
      },
    },
  );
};

export const useDeletePurchaseAgent = () => {
  const queryClient = useQueryClient();
  const snackbar = useCustomSnackbarGlobal();
  const { t } = useTranslation();

  const mutation = useMutation(
    (purchaseAgentId: string) => ApiService.purchaseAgent.purchaseAgentControllerDelete(purchaseAgentId),
    {
      onMutate: async (targetId: string) => {
        await queryClient.cancelQueries([QueryKeys.purchaseAgents]);

        const snapshot = queryClient.getQueryData<PurchaseAgentListItem[]>([QueryKeys.purchaseAgents]);

        queryClient.setQueryData<PurchaseAgentListItem[]>([QueryKeys.purchaseAgents], (prev = []) =>
          prev.filter((pa) => pa.id?.purchaseAgentId !== targetId),
        );

        return snapshot;
      },
      onError: (error, _targetId, snapshot) => {
        console.error('[usePurchaseAgents] deletePurchaseAgent failed', error);
        queryClient.setQueryData<PurchaseAgentListItem[] | undefined>([QueryKeys.purchaseAgents], snapshot);
        snackbar.showError(t('purchaseAgent.delete.error'));
      },
      onSettled: () => {
        queryClient.invalidateQueries([QueryKeys.purchaseAgents]);
      },
    },
  );

  return mutation;
};

export const useUpdatePurchaseAgent = () => {
  const queryClient = useQueryClient();
  const snackbar = useCustomSnackbarGlobal();
  const { t } = useTranslation();

  return useMutation(
    ({ id, body }: { id: string; body: UpdatePurchaseAgentRequest }) =>
      ApiService.purchaseAgent.purchaseAgentControllerUpdate(id, body),
    {
      onSuccess: () => {
        snackbar.showSuccess(t('purchaseAgent.messages.updateSuccess'));
      },
      onError: (error) => {
        console.error('[usePurchaseAgents] updatePurchaseAgent failed', error);
        snackbar.showError(
          t(
            getErrorCode(error) === 'BUDGET_CURRENCY_CHANGE_NOT_ALLOWED'
              ? 'purchaseAgent.messages.errorCodes.budget_currency_change_not_allowed'
              : 'purchaseAgent.messages.updateError',
          ),
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries([QueryKeys.purchaseAgents]);
        queryClient.invalidateQueries([QueryKeys.purchaseAgentDetail]);
      },
    },
  );
};

export const usePurchaseAgentLimits = (id?: string, { enabled = true }: { enabled?: boolean } = {}) =>
  useQuery(
    [QueryKeys.purchaseAgentLimits, id],
    () => ApiService.purchaseAgent.purchaseAgentControllerLimits(id!).then((res) => res.data),
    { enabled: enabled && !!id },
  );

const CHANGE_STATE_MUTATION_KEY = [QueryKeys.purchaseAgents, 'changeState'];

const CHANGE_STATE_REQUESTS: Record<PurchaseAgentAction, (id: string) => Promise<unknown>> = {
  activate: (id) => ApiService.purchaseAgent.purchaseAgentControllerActivate(id),
  deactivate: (id) => ApiService.purchaseAgent.purchaseAgentControllerDeactivate(id),
  pause: (id) => ApiService.purchaseAgent.purchaseAgentControllerPause(id),
  resume: (id) => ApiService.purchaseAgent.purchaseAgentControllerResume(id),
};

type ChangePurchaseAgentStateVariables = { id: string; action: PurchaseAgentAction };

// The translation key for a BE error code that tells the user why a state change failed, if it is a known one.
export const getChangeStateErrorCodeKey = (error: unknown) => {
  const errorCode = getErrorCode(error);
  if (errorCode === 'SEARCH_AGENT_DEACTIVATED') return 'purchaseAgent.activate.errorCodes.search_agent_deactivated';
  if (errorCode === 'INVALID_STATE_TRANSITION') return 'purchaseAgent.activate.errorCodes.invalid_state_transition';
  return undefined;
};

export const useChangePurchaseAgentState = () => {
  const queryClient = useQueryClient();
  const snackbar = useCustomSnackbarGlobal();
  const { t } = useTranslation();

  return useMutation(({ id, action }: ChangePurchaseAgentStateVariables) => CHANGE_STATE_REQUESTS[action](id), {
    mutationKey: CHANGE_STATE_MUTATION_KEY,
    onMutate: async ({ id, action }) => {
      await queryClient.cancelQueries([QueryKeys.purchaseAgents]);
      const snapshot = queryClient.getQueryData<PurchaseAgentListItem[]>([QueryKeys.purchaseAgents]);
      queryClient.setQueryData<PurchaseAgentListItem[]>([QueryKeys.purchaseAgents], (prev = []) =>
        prev.map((pa) =>
          pa.id?.purchaseAgentId === id
            ? {
                ...pa,
                state: PURCHASE_AGENT_ACTION_TARGET_STATE[action],
                // activate starts a new cycle, the old usage must not flash until the refetch
                ...(action === 'activate' && { limitUsage: undefined }),
              }
            : pa,
        ),
      );
      return snapshot;
    },
    onError: (error, { action }, snapshot) => {
      console.error(`[usePurchaseAgents] changePurchaseAgentState (${action}) failed`, error);
      queryClient.setQueryData<PurchaseAgentListItem[] | undefined>([QueryKeys.purchaseAgents], snapshot);

      snackbar.showError(t(getChangeStateErrorCodeKey(error) ?? (`purchaseAgent.${action}.error` as const)));
    },
    onSettled: () => {
      queryClient.invalidateQueries([QueryKeys.purchaseAgents]);
      queryClient.invalidateQueries([QueryKeys.purchaseAgentDetail]);
      queryClient.invalidateQueries([QueryKeys.purchaseAgentLimits]);
    },
  });
};

// Each button owns its own mutation instance, so a row asks the mutation cache whether any state
// change for its agent is still running.
export const useIsPurchaseAgentStateChanging = (id?: string) =>
  useIsMutating({
    mutationKey: CHANGE_STATE_MUTATION_KEY,
    predicate: (mutation) => (mutation.state.variables as ChangePurchaseAgentStateVariables | undefined)?.id === id,
  }) > 0;
